/**
* PURPOSE: Sección administrativa de listado y gestión de usuarios.
* RESPONSIBILITIES: Buscar, ordenar y abrir modales para edición de usuario, permisos de companies, eventos/deportes y sidebar.
* COLLABORATORS: src/services/user.js, src/utils/userState.js, src/utils/userHashtag.js, src/admin/CreateUser/Modal/ModalEditUser/ModalEditUser.jsx, src/admin/CreateUser/Modal/ModalUpdatePermisosCompany/ModalUpdatePermisosCompany.jsx
* ROUTE: src/sections/UserSection.jsx
*/
import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";

// icons
import FestivalIcon from "@mui/icons-material/Festival";
import WidgetsIcon from "@mui/icons-material/Widgets";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import BusinessIcon from "@mui/icons-material/Business";
import Tooltip from "@mui/material/Tooltip";

// services / utils
import { getUsers } from "../services/user.js";
import { getUserStateMeta, getUserStateOrder } from "../utils/userState.js";
import { parseUserHashtags } from "../utils/userHashtag.js";

// modals
import ModalUpdatePermisosEventSport from "../admin/CreateUser/Modal/ModalUpdatePermisosEventSport/ModalUpdatePermisosEventSport";
import ModalGivePermisosMenu from "../admin/CreateUser/Modal/ModalGivePermisosUser/ModalGivePermisosUser";
import ModalEditUser from "../admin/CreateUser/Modal/ModalEditUser/ModalEditUser.jsx";
import ModalUpdatePermisosCompany from "../admin/CreateUser/Modal/ModalUpdatePermisosCompany/ModalUpdatePermisosCompany.jsx";

const ROLE_ORDER = {
    1: 1,
    4: 2,
    2: 3,
    3: 4,
};

const getRoleOrder = (rol) => ROLE_ORDER[Number(rol)] || 99;

const sortUsers = (items = []) => {
    return [...items].sort((a, b) => {
        const byState = getUserStateOrder(a?.state) - getUserStateOrder(b?.state);
        if (byState !== 0) return byState;

        const byRole = getRoleOrder(a?.rol) - getRoleOrder(b?.rol);
        if (byRole !== 0) return byRole;

        return String(a?.username || "").localeCompare(String(b?.username || ""));
    });
};

const buildSearchText = (user) => {
    return [
        user?.username,
        user?.rol,
        user?.tag_rol,
        user?.firstname,
        user?.surname,
        user?.phone,
        user?.email,
        user?.idcompany,
        user?.hashtag,
    ]
        .filter((value) => value !== null && value !== undefined)
        .join(" ")
        .toLowerCase();
};

const UsersSection = () => {
    const [users, setUsers] = useState([]);
    const [search, setSearch] = useState("");
    const [userSelect, setUserSelect] = useState(null);
    const [loading, setLoading] = useState(true);

    const [openPermisosEventSport, setOpenPermisosEventSport] = useState(false);
    const [openPermisosMenu, setOpenPermisosMenu] = useState(false);
    const [openEditUser, setOpenEditUser] = useState(false);
    const [openPermisosCompany, setOpenPermisosCompany] = useState(false);

    const loadUsers = async () => {
        try {
            setLoading(true);
            const data = await getUsers();
            setUsers(sortUsers(Array.isArray(data) ? data : []));
        } catch (error) {
            console.error("Error al traer usuarios", error);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadUsers();
    }, []);

    const filteredUsers = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return sortUsers(users);

        return sortUsers(users).filter((user) => buildSearchText(user).includes(query));
    }, [search, users]);

    const handleClickEditUser = (user) => {
        setUserSelect(user);
        setOpenEditUser(true);
    };

    const handleClickPermisosCompany = (user) => {
        setUserSelect(user);
        setOpenPermisosCompany(true);
    };

    const handleClickPermisosEventSport = (user) => {
        setUserSelect(user);
        setOpenPermisosEventSport(true);
    };

    const handleClickPermisosMenu = (user) => {
        setUserSelect(user);
        setOpenPermisosMenu(true);
    };

    return (
        <div className="p-6">
            <h1 className="mb-6 text-2xl font-bold text-gray-800">Lista de Usuarios</h1>

            <div className="relative mb-6">
                <input
                    type="text"
                    placeholder="Buscar por username, nombre, rol, email o hashtag..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 pl-10 shadow-sm transition duration-200 ease-in-out focus:border-blue-500 focus:outline-none focus:ring focus:ring-blue-200"
                />
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400" />
            </div>

            {loading ? (
                <p className="text-sm text-gray-500">Cargando usuarios...</p>
            ) : null}

            {!loading ? (
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {filteredUsers.map((user) => {
                        const stateMeta = getUserStateMeta(user?.state);
                        const hashtags = parseUserHashtags(user?.hashtag);
                        const oneCompany = user?.is_one_company === true || Number(user?.is_one_company) === 1;

                        return (
                            <div
                                key={user.iduser}
                                className="rounded-lg border border-gray-200 bg-white p-4 shadow-md transition-shadow duration-200 ease-in-out hover:shadow-lg"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <h2 className="truncate text-lg font-semibold text-gray-700">
                                            <span className="text-gray-900">{user.username}</span>
                                        </h2>
                                        <p className="mt-1 text-sm text-gray-500">
                                            {user.firstname || ""} {user.surname || ""}
                                        </p>
                                    </div>

                                    <span className={`inline-flex shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${stateMeta.className}`}>
                                        {stateMeta.label}
                                    </span>
                                </div>

                                <div className="mt-3 space-y-1 text-sm text-gray-600">
                                    <p>
                                        <strong>Rol:</strong> {user.rol} {user.tag_rol ? `· ${user.tag_rol}` : ""}
                                    </p>
                                    <p>
                                        <strong>Company:</strong> {oneCompany ? `Única (${user.idcompany || "sin id"})` : "Múltiple"}
                                    </p>
                                    {user.email ? (
                                        <p className="truncate">
                                            <strong>Email:</strong> {user.email}
                                        </p>
                                    ) : null}
                                    {user.phone ? (
                                        <p>
                                            <strong>Celular:</strong> {user.phone}
                                        </p>
                                    ) : null}
                                    <p>
                                        <strong>Creado:</strong> {user.createdAt ? format(new Date(user.createdAt), "dd/MM/yyyy") : "-"}
                                    </p>
                                </div>

                                {hashtags.length > 0 ? (
                                    <div className="mt-3 flex flex-wrap gap-1.5">
                                        {hashtags.map((tag) => (
                                            <span
                                                key={tag}
                                                className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700 ring-1 ring-indigo-100"
                                            >
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                ) : null}

                                <div className="mt-4 flex flex-wrap gap-3">
                                    <Tooltip title="Editar datos del usuario" arrow>
                                        <button
                                            onClick={() => handleClickEditUser(user)}
                                            className="rounded-full bg-slate-700 p-2 text-white shadow-md transition duration-200 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500"
                                        >
                                            <EditIcon />
                                        </button>
                                    </Tooltip>

                                    <Tooltip title="Habilitar organizaciones" arrow>
                                        <button
                                            onClick={() => handleClickPermisosCompany(user)}
                                            className="rounded-full bg-purple-600 p-2 text-white shadow-md transition duration-200 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                        >
                                            <BusinessIcon />
                                        </button>
                                    </Tooltip>

                                    <Tooltip title="Añadir permiso para evento o deporte" arrow>
                                        <button
                                            onClick={() => handleClickPermisosEventSport(user)}
                                            className="rounded-full bg-blue-600 p-2 text-white shadow-md transition duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <FestivalIcon />
                                        </button>
                                    </Tooltip>

                                    <Tooltip title="Añadir permiso para Menu" arrow>
                                        <button
                                            onClick={() => handleClickPermisosMenu(user)}
                                            className="rounded-full bg-green-600 p-2 text-white shadow-md transition duration-200 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                                        >
                                            <WidgetsIcon />
                                        </button>
                                    </Tooltip>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : null}

            {!loading && filteredUsers.length === 0 ? (
                <p className="mt-6 text-center text-gray-600">
                    No se encontraron usuarios con el criterio "{search}".
                </p>
            ) : null}

            {openEditUser && userSelect ? (
                <ModalEditUser
                    open={openEditUser}
                    setOpen={setOpenEditUser}
                    user={userSelect}
                    onUpdated={loadUsers}
                />
            ) : null}

            {openPermisosCompany && userSelect ? (
                <ModalUpdatePermisosCompany
                    open={openPermisosCompany}
                    setOpen={setOpenPermisosCompany}
                    user={userSelect}
                />
            ) : null}

            {openPermisosEventSport && userSelect ? (
                <ModalUpdatePermisosEventSport
                    open={openPermisosEventSport}
                    setOpen={setOpenPermisosEventSport}
                    user={userSelect}
                />
            ) : null}

            {openPermisosMenu && userSelect ? (
                <ModalGivePermisosMenu
                    open={openPermisosMenu}
                    setOpen={setOpenPermisosMenu}
                    user={userSelect}
                />
            ) : null}
        </div>
    );
};

export default UsersSection;
